import { useState } from 'react'
import { Bug, HelpCircle, Lightbulb, X } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { DsIsland } from '@/design-system/island'
import { Button, Field, Input, Notice, Segmented, Textarea } from '@/design-system/primitives'
import { Moment } from '@/design-system/feedback'
import '@/design-system/settings.css'
import '@/design-system/support.css'
import '@/design-system/form.css'

interface SupportModalProps {
  isOpen: boolean
  onClose: () => void
}

const CATEGORY_OPTIONS = [
  {
    value: 'general',
    label: 'Question',
    description: 'Setup or account help.',
    icon: HelpCircle
  },
  {
    value: 'bug_report',
    label: 'Issue',
    description: 'Something is not working.',
    icon: Bug
  },
  {
    value: 'feature_request',
    label: 'Idea',
    description: 'Product feedback.',
    icon: Lightbulb
  }
] as const

const URGENCY_OPTIONS = [
  { value: 'normal', label: 'Normal', description: 'A reply within one business day is okay.' },
  { value: 'high', label: 'High', description: 'This is blocking active setup or deployment.' },
  { value: 'low', label: 'Low', description: 'Feedback or a non-urgent question.' }
] as const

type SupportCategory = typeof CATEGORY_OPTIONS[number]['value']

const MAX_CHARS = 1500
const MIN_CHARS = 10

export default function SupportModal({ isOpen, onClose }: SupportModalProps) {
  const [category, setCategory] = useState<SupportCategory | ''>('')
  const [urgency, setUrgency] = useState<typeof URGENCY_OPTIONS[number]['value']>('normal')
  const [affectedUrl, setAffectedUrl] = useState('')
  const [workflowContext, setWorkflowContext] = useState('')
  const [message, setMessage] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)

  const createTicket = useMutation({
    mutationFn: (data: { category: 'general' | 'feature_request' | 'bug_report'; message: string }) =>
      apiService.createSupportTicket(data),
    onSuccess: () => {
      setShowSuccess(true)
    }
  })

  const handleClose = () => {
    setCategory('')
    setUrgency('normal')
    setAffectedUrl('')
    setWorkflowContext('')
    setMessage('')
    setShowSuccess(false)
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!category || message.length < MIN_CHARS) return

    const categoryLabel = CATEGORY_OPTIONS.find((option) => option.value === category)?.label || category
    const urgencyLabel = URGENCY_OPTIONS.find((option) => option.value === urgency)?.label || urgency
    const enrichedMessage = [
      `Request type: ${categoryLabel}`,
      `Urgency: ${urgencyLabel}`,
      affectedUrl.trim() ? `Relevant page or URL: ${affectedUrl.trim()}` : null,
      workflowContext.trim() ? `What they were trying to do: ${workflowContext.trim()}` : null,
      '',
      'Customer message:',
      message.trim()
    ].filter(Boolean).join('\n')

    createTicket.mutate({
      category,
      message: enrichedMessage
    })
  }

  const charCount = message.length
  const isValid = Boolean(category) && charCount >= MIN_CHARS && charCount <= MAX_CHARS
  const selectedUrgency = URGENCY_OPTIONS.find((option) => option.value === urgency)

  if (!isOpen) return null

  return (
    /*
      The support form. It opened with a navy plate holding a chartreuse life
      ring, a monospace uppercase "CUSTOMER CARE" over a serif headline, and a
      purple blur bleeding out of the top-right corner. Three category cards
      turned solid indigo when picked, the urgency pills did the same, and the
      note about not sending credentials sat on a chartreuse wash.

      What somebody opening this actually needs is to describe a problem and
      press send, so the form is the whole surface and the only colour left is
      on the one line that is a warning.
    */
    <DsIsland panel={false} ground={false} className="ds-help">
      <div className="ds-help__scrim" onClick={handleClose} />

      <div
        className="ds-help__sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
      >
        <div className="ds-help__head">
          <div>
            <h2 id="support-modal-title" className="ds-h3">Tell us what you need</h2>
            <p className="ds-help__lead">Share the context we need to help quickly.</p>
          </div>
          <Button type="button" variant="ghost" size="sm" icon onClick={handleClose} aria-label="Close support form">
            <X aria-hidden="true" />
          </Button>
        </div>

        {showSuccess ? (
          <div className="ds-help__body">
            <Moment
              title="We received your request"
              detail="We will review the account context and reply by email with the next best step."
            >
              <div className="ds-help__contact">
                <p>
                  Support replies come from <b>trillion@superschema.ai</b>. Add it to your contacts
                  or safe sender list so our reply does not land in spam.
                </p>
              </div>
              <Button type="button" onClick={handleClose}>Done</Button>
            </Moment>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="ds-help__form">
            <div className="ds-help__body">
              <div className="ds-fields">
                <fieldset className="ds-help__kinds">
                  <legend className="ds-label">What can we help with?</legend>
                  <div className="ds-help__kindgrid">
                    {CATEGORY_OPTIONS.map((opt) => {
                      const Icon = opt.icon
                      const isSelected = category === opt.value
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setCategory(opt.value)}
                          aria-pressed={isSelected}
                          className="ds-help__kind"
                        >
                          <span className="ds-help__kindmark" aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="ds-help__kindwhat">
                            <span className="ds-help__kindname">{opt.label}</span>
                            <span className="ds-help__kindwhy">{opt.description}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </fieldset>

                <Field
                  label="Relevant page or URL"
                  hint="Optional, but very helpful for schema, HubSpot, scan, or verification issues."
                >
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={affectedUrl}
                      onChange={(e) => setAffectedUrl(e.target.value)}
                      placeholder="https://example.com/page"
                    />
                  )}
                </Field>

                <div className="ds-field">
                  <span className="ds-field__label">Urgency</span>
                  <Segmented
                    label="Urgency"
                    value={urgency}
                    onChange={setUrgency}
                    options={URGENCY_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
                  />
                  <p className="ds-field__hint">{selectedUrgency?.description}</p>
                </div>

                <Field label="What were you trying to do?">
                  {(id) => (
                    <Input
                      id={id}
                      value={workflowContext}
                      onChange={(e) => setWorkflowContext(e.target.value)}
                      placeholder="Deploy schema to HubSpot, verify a page, run a Smart Scan"
                    />
                  )}
                </Field>

                <Field
                  label="What happened?"
                  aside={charCount < MIN_CHARS ? `${MIN_CHARS} characters minimum` : `${charCount}/${MAX_CHARS}`}
                  hint="Helpful: steps, page URL, screenshot notes, and exact error text."
                >
                  {(id, describedBy) => (
                    <Textarea
                      id={id}
                      aria-describedby={describedBy}
                      value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
                      placeholder="Tell us what you expected, what happened instead, and any error text you saw. The more detail you share, the faster we can help."
                      rows={9}
                      required
                    />
                  )}
                </Field>

                {/* The one line on this form that is a warning, so the one
                    thing on it that takes a colour. */}
                <Notice kind="look">
                  We include your account context with the ticket. Please avoid sending passwords,
                  API keys, or private credentials.
                </Notice>

                {createTicket.isError && (
                  <Notice kind="look">
                    We could not send this yet. Please try again, and if it still fails, email
                    support directly at trillion@superschema.ai.
                  </Notice>
                )}
              </div>
            </div>

            <div className="ds-help__foot">
              <p className="ds-help__when">We usually reply within one business day.</p>
              <div className="ds-help__acts">
                <Button type="button" variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isValid} loading={createTicket.isPending}>
                  Send request
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </DsIsland>
  )
}
